import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ExternalLink, Phone, Facebook, Instagram, Globe } from "lucide-react";
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from "./NewsTicker";
import SocialLinks, { SOCIAL_FIELDS } from "./SocialLinks";

const AUTOMOTIVE_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "description", label: "Services offered", placeholder: "Auto repair, detailing, tires, body shop" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "phone", label: "Contact phone", placeholder: "(469) 343-4228" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  { key: "socialMediaUrl", label: "Social media URL", type: "url", placeholder: "Facebook, Instagram, or website link — shows as a big button" },
  ...SOCIAL_FIELDS,
];

export interface AutomotiveEntry {
  name: string;
  description: string;
  website?: string;
  address?: string;
  phone?: string;
  image: string;
  hideViewLocation?: boolean;
  instagram?: string;
  socialMediaUrl?: string;
}

function socialButtonMeta(url: string) {
  const lower = url.toLowerCase();
  if (lower.includes("facebook.com") || lower.includes("fb.com")) {
    return { label: "Facebook", Icon: Facebook, className: "bg-[#1877F2] hover:bg-[#0f5fcc]" };
  }
  if (lower.includes("instagram.com")) {
    return {
      label: "Instagram",
      Icon: Instagram,
      className: "bg-gradient-to-r from-[#f58529] via-[#dd2a7b] to-[#8134af] hover:opacity-90",
    };
  }
  return { label: "Visit Page", Icon: Globe, className: "bg-[#f59e0b] hover:bg-[#d98a08]" };
}

export const AUTOMOTIVE_ENTRIES: AutomotiveEntry[] = [
  {
    name: "Via Tire",
    description: "Full-service tire shop and mobile tire company serving drivers throughout DFW",
    address: "685 S Stemmons Fwy, Lewisville, TX 75067",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-08-01/224e5d200a7f.jpeg",
  },
  {
    name: "FAIRKAR INC",
    description: "Used Car Dealership in Plano, TX",
    website: "https://www.fairkar.com/",
    address: "4421 Tradition Trail, Plano, TX 75093",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-09/489c9b187bda.jpeg",
  },
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=600&h=400&fit=crop";

function mapsUrl(e: AutomotiveEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address}`)}`;
}

export function AutomotiveCard({ entry, index }: { entry: AutomotiveEntry; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full h-[180px] relative overflow-hidden bg-gray-100 flex items-center justify-center">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-1.5 pb-0.5 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="text-[10px] font-bold text-[#082f6d] leading-tight truncate">
            <a
              href={`/automotive-services/${slugify(entry.name || "")}`}
              className="hover:underline"
            >
              {entry.name}
            </a>
          </h3>
          {entry.description && (
            <p className="text-[9px] text-black font-medium leading-snug mt-0.5">
              {entry.description}
            </p>
          )}
          {entry.address && (
            <div className="flex items-start gap-0.5 mt-0.5">
              <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-[9px] text-gray-500 font-medium leading-snug truncate">
                {entry.address}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="px-1.5 pb-1.5 pt-0.5 flex flex-col gap-1">
        <SocialLinks item={{ ...entry, website: undefined }} px={20} />
        <div className="flex flex-wrap gap-1.5">
          {entry.website && (
            <form action={entry.website} method="get" target="_top" className="flex-1 min-w-[70px]">
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-1 bg-[#082f6d] hover:bg-[#062454] text-white text-[9px] font-semibold py-1.5 px-1.5 rounded-md transition-colors duration-200 cursor-pointer"
              >
                <ExternalLink className="w-2.5 h-2.5" /> Website
              </button>
            </form>
          )}
          {entry.phone && (
            <a
              href={`tel:${entry.phone.replace(/[^\d+]/g, "")}`}
              className="flex-1 min-w-[70px] flex items-center justify-center gap-1 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[9px] font-semibold py-1.5 px-1.5 rounded-md transition-colors duration-200 cursor-pointer"
            >
              <Phone className="w-2.5 h-2.5" /> Call
            </a>
          )}
          {entry.socialMediaUrl && (() => {
            const { label, Icon, className } = socialButtonMeta(entry.socialMediaUrl!);
            return (
              <a
                href={entry.socialMediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex-1 min-w-[70px] flex items-center justify-center gap-1 ${className} text-white text-[9px] font-semibold py-1.5 px-1.5 rounded-md transition-colors duration-200 cursor-pointer`}
              >
                <Icon className="w-2.5 h-2.5" /> {label}
              </a>
            );
          })()}
          {entry.address && !entry.hideViewLocation && (
            <a
              href={mapsUrl(entry)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 min-w-[70px] flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-[9px] font-semibold py-1.5 px-1.5 rounded-md transition-colors duration-200"
            >
              <ExternalLink className="w-2.5 h-2.5" /> View Location
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function AutomotiveServices() {
  const [query, setQuery] = useState("");
  const mc = useManagedCollection<any>("automotive_services", AUTOMOTIVE_ENTRIES, (e, i) => slugify(e.name || String(i)));

  const filtered = mc.items.filter(
    (e) =>
      query.trim() === "" ||
      e.name.toLowerCase().includes(query.trim().toLowerCase()) ||
      (e.description || "").toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Automotive Services"
        placeholder="Search auto repair, detailing, tires…"
        backgroundImage="https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=1440&h=400&fit=crop"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Automotive Services
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              Find South Asian auto repair, detailing, tire, and body shop professionals in DFW
            </p>
            <AdminAddBar mc={mc} fields={AUTOMOTIVE_FIELDS} label="Add Listing" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                {filtered.map((e, i) => (
                  <AdminItem key={e._key} mc={mc} item={e} fields={AUTOMOTIVE_FIELDS} title="Listing">
                    <AutomotiveCard entry={e as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No automotive service listings yet.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
